import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SeoHeatmap } from '@/components/seo/SeoHeatmap';
import { SeoSeasonChart } from '@/components/seo/SeoSeasonChart';
import { compareNote, deltaTone, esDate } from '@/components/seo/format';
import type { SeoReport } from '@/components/seo/types';
import { useSeoAudit } from '@/hooks/useSeoAudit';
import { cn } from '@/lib/utils';

const WEEK_WINDOWS = [12, 24, 52];
const DAY_WINDOWS = [14, 28, 90];

export function SeoAuditTab() {
    const [grain, setGrain] = useState<'week' | 'day'>('week');
    const [weeks, setWeeks] = useState(12);
    const [days, setDays] = useState(28);
    const { data, isLoading, error } = useSeoAudit(weeks, grain === 'day' ? days : 0);
    const windows = grain === 'day' ? DAY_WINDOWS : WEEK_WINDOWS;
    const selected = grain === 'day' ? days : weeks;

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-1 rounded-lg bg-muted/50 p-1">
                    {(['week', 'day'] as const).map((value) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => setGrain(value)}
                            className={cn(
                                'rounded-md px-3 py-1.5 text-sm font-medium',
                                grain === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                            )}
                        >
                            {value === 'week' ? 'Semanas' : 'Días'}
                        </button>
                    ))}
                </div>
                <div className="flex gap-1 rounded-lg bg-muted/50 p-1">
                    {windows.map((value) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => (grain === 'day' ? setDays(value) : setWeeks(value))}
                            className={cn(
                                'rounded-md px-3 py-1.5 text-sm font-medium',
                                selected === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                            )}
                        >
                            {value} {grain === 'day' ? 'días' : 'sem'}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading && <Loader2 className="animate-spin text-muted-foreground" size={18} />}
            {error && (
                <p className="text-sm text-rose-500">
                    {error instanceof Error ? error.message : 'No se pudo cargar la serie SEO.'}
                </p>
            )}
            {data && !data.weeks.length && (
                <p className="text-sm text-muted-foreground">
                    {grain === 'day'
                        ? 'Aún no hay días guardados. El cron de las 02:00 UTC irá llenando la tabla.'
                        : 'Aún no hay semanas guardadas. El cron de las 02:00 UTC irá llenando la tabla.'}
                </p>
            )}
            {data && data.weeks.length > 0 && <SeoAuditBody report={data} />}
        </div>
    );
}

function SeoAuditBody({ report }: { report: SeoReport }) {
    return (
        <div className="space-y-6">
            <div>
                <p className="text-sm text-muted-foreground">
                    Del {esDate(report.period.start)} al {esDate(report.period.end)}
                    {report.data_through ? ` · datos hasta ${esDate(report.data_through)}` : ''}
                    {report.generated_at ? ` · sincronizado ${esDate(report.generated_at)}` : ''}
                </p>
                <div className="mt-3 space-y-1">
                    {report.lines.map((line) => (
                        <p key={line} className="text-sm text-foreground">{line}</p>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {report.cards.map((card) => {
                    const tone = deltaTone(card.id, card.delta);
                    return (
                        <article key={card.id} className="rounded-xl border border-border bg-card p-4">
                            <p className="text-sm text-muted-foreground">{card.label}</p>
                            <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{card.after_txt}</p>
                            <p className={cn(
                                'mt-1 text-sm tabular-nums',
                                tone === 'up' && 'text-success',
                                tone === 'down' && 'text-danger',
                                tone === 'flat' && 'text-muted-foreground',
                            )}>
                                {report.compare.before_label}: {card.before_txt} · {card.delta_txt}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">{card.hint}</p>
                        </article>
                    );
                })}
            </div>
            <p className="text-sm text-muted-foreground">{compareNote(report)}</p>

            <div>
                <h2 className="text-lg font-semibold">Control de temporada</h2>
                <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <SeoSeasonChart
                        title={report.grain === 'day' ? 'Clics por día' : 'Clics por semana'}
                        caption=""
                        report={report}
                        field="clicks"
                        yoyField="yoy_clicks"
                        invert={false}
                        digits={0}
                    />
                    <SeoSeasonChart
                        title="Posición media"
                        caption=""
                        report={report}
                        field="position"
                        yoyField="yoy_position"
                        invert
                        digits={1}
                    />
                </div>
            </div>

            <div>
                <h2 className="mb-3 text-lg font-semibold">Mapa por bloque</h2>
                <SeoHeatmap report={report} />
            </div>
        </div>
    );
}
